<template>
  <div
    :class="[
      'grid gap-0 items-center border-b border-gray-200 cursor-pointer transition-colors duration-150',
      'grid-cols-[20rem_repeat(5,minmax(5rem,1fr))_repeat(3,minmax(4.5rem,1fr))]',
      selected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
    ]"
    @click="$emit('select', project)"
  >
    <div class="px-6 py-4 text-wrap text-sm text-gray-900">
      <div class="flex items-center space-x-1">
        <RouterLink
          v-if="hasSettingsAccess"
          :to="{ name: 'ProjectSettings', params: { projectId: project.id } }"
          class="btn-icon btn-icon-default"
          @click.stop=""
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>
        </RouterLink>

        <span class="font-medium text-gray-900">{{ project.name }}</span>
      </div>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span class="text-sm text-gray-600">{{ project.reference }}</span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span
        :class="[
          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
          !project.archived_at ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        ]"
      >
        <svg
    fill="currentColor"
    viewBox="0 0 20 20"
 v-if="!project.archived_at"
         
          class="w-3 h-3 mr-1">
<path
    fill-rule="evenodd"
    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
    clip-rule="evenodd"
/>
</svg>

        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
     v-else
         
          class="w-3 h-3 mr-1">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
</svg>
        {{ !project.archived_at ? $t('ProjectSelector.ProjectDashboard.live') : $t('ProjectSelector.ProjectDashboard.archived') }}
      </span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span class="text-sm text-gray-600">{{ formatDate(project.contract_start_date) }}</span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span class="text-sm text-gray-600">{{ formatDate(project.contract_end_date) }}</span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span class="text-sm text-gray-600">{{ getFinancialYear(project.contract_start_date, project.contract_end_date) }}</span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
      <span class="font-medium capitalize">
        {{ $tc(totalBudget) }}
      </span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
      <span class="font-medium capitalize">
        {{ $tc(totalCertified) }}
      </span>
    </div>

    <div class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
      <span
        class="font-medium capitalize"
        :class="[profit > 0 ? 'text-green-600' : profit === 0 ? 'text-gray-600' : 'text-red-600']"
      >
        {{ $tc(profit) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import Project from '@/models/Project';
import { useUserStore } from '@/store/user';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps<{
  project: Project;
  profit: number;
  totalBudget: number;
  totalCertified: number;
  selected?: boolean;
}>();

defineEmits<{
  (e: 'select', project: Project): void;
}>();

const userStore = useUserStore();

const hasSettingsAccess = computed(() => {
  return userStore.hasAccessToRoute('ProjectSettings', props.project.id);
});

const formatDate = (dateString: string | undefined) => {
  if(!dateString) return '-';

  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getFinancialYear = (startDateString: string | undefined, endDateString: string | undefined) => {
  if(!startDateString) return '-';

  const startDate = new Date(startDateString);
  const endDate = endDateString ? new Date(endDateString) : undefined;

  const getFinancialYearForDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if(month > 7 || (month === 7 && day >= 1)) {
      return year + 1;
    }

    return year;
  };

  const startFinancialYear = getFinancialYearForDate(startDate);

  if(!endDate) {
    return startFinancialYear.toString();
  }

  const endFinancialYear = getFinancialYearForDate(endDate);

  if(startFinancialYear === endFinancialYear) {
    return startFinancialYear.toString();
  }

  return `${startFinancialYear}-${endFinancialYear}`;
};
</script>
